import React, { useEffect, useState } from 'react';
import { getDominantColor } from '../utils/badgeColor';

const TeamBadge = ({ teamId, teamName, isPostponed = false, isFlag = false, simple = false, plain = false, shadowColor = null, grayscale = false }) => {
  const [hasError, setHasError] = useState(false);
  const [shadow, setShadow] = useState(null);
  const [dropShadow, setDropShadow] = useState(null);
  const [flagScale, setFlagScale] = useState(null);
  // Na telefonu (stejný breakpoint jako CSS MatchCard) dáváme menší stín loga.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 1000px)').matches
  );
  const backendUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, '');
  const badgePath = `${backendUrl}/badges/${teamId}.png`;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mql = window.matchMedia('(max-width: 1000px)');
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Stín pod logem může barevně sladit volající (MatchCard po deconfliktu barev
  // posílá tu skutečně použitou barvu pozadí přes `shadowColor`), jinak padne na
  // vlastní dominantní barvu. Klíč drží efekt stabilní mezi rendery.
  const shadowKey = shadowColor ? shadowColor.join(',') : '';

  // Z badge si vytáhneme dominantní barvu (pro barevný stín pod kruhem) a
  // u vlajek i přesný zoom, aby vlajka vyplnila celý kruh (letterbox v PNG
  // je u různých vlajek různě velký). U odložených zápasů (grayscale) stín
  // vynecháme. Hodnoty se cachují v utilu podle URL.
  useEffect(() => {
    let cancelled = false;
    getDominantColor(teamId, teamName).then(({ color, fillScale }) => {
      if (cancelled) return;
      setFlagScale(fillScale);
      if (!isPostponed) {
        const [r, g, b] = shadowColor || color;
        // Menší stín na telefonu, výraznější na desktopu.
        if (isMobile) {
          setShadow(`0 4px 10px -4px rgba(${r}, ${g}, ${b}, 0.4), 0 1px 3px rgba(0, 0, 0, 0.12)`);
          setDropShadow(`drop-shadow(0 2px 3px rgba(${r}, ${g}, ${b}, 0.4)) drop-shadow(0 1px 1px rgba(0, 0, 0, 0.2))`);
        } else {
          setShadow(`0 9px 22px -4px rgba(${r}, ${g}, ${b}, 0.45), 0 2px 6px rgba(0, 0, 0, 0.14)`);
          // Pro čisté logo (bez kruhu) použijeme drop-shadow filtr, který kopíruje
          // průhledný tvar loga (ne obdélník jako box-shadow).
          setDropShadow(`drop-shadow(0 5px 6px rgba(${r}, ${g}, ${b}, 0.45)) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25))`);
        }
      }
    });
    return () => { cancelled = true; };
  }, [teamId, teamName, isPostponed, isMobile, shadowKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (hasError) {
    return (
      <div
        className={`team-badge-placeholder ${(simple || plain) ? 'is-simple' : ''} ${plain ? 'is-plain' : ''}`}
        style={{
          backgroundColor: isPostponed ? '#e0e0e0' : '#f0f0f0',
          color: isPostponed ? '#888' : '#555',
          filter: (isPostponed || grayscale) ? 'grayscale(100%)' : 'none'
        }}
      >
        {teamName ? teamName.slice(0, 2).toUpperCase() : '??'}
      </div>
    );
  }

  // Jednoduchá varianta: badge bez kruhového rámečku – jen logo tak, jak ho
  // vrací backend. Používá se v tabulce Standings a v přehledu posledních
  // zápasů týmu. Kruhový rámeček (níže) zůstává jen na MatchCard.
  if (simple) {
    return (
      <img
        src={badgePath}
        alt={`Badge for ${teamName}`}
        className="team-badge-simple"
        style={{
          filter: (isPostponed || grayscale) ? 'grayscale(100%)' : 'none',
          opacity: isPostponed ? 0.7 : 1
        }}
        onError={() => setHasError(true)}
      />
    );
  }

  // Čistá varianta: logo bez kruhového rámečku, jen s jemným barevným stínem
  // (drop-shadow kopíruje průhledný tvar loga). Používá se na MatchCard pro
  // nemezinárodní turnaje – klubová loga tak vyniknou bez kolečka.
  if (plain) {
    const filters = [];
    if (isPostponed) filters.push('grayscale(100%)');
    else {
      if (grayscale) filters.push('grayscale(100%)');
      if (dropShadow) filters.push(dropShadow);
    }
    return (
      <img
        src={badgePath}
        alt={`Badge for ${teamName}`}
        className="team-badge-plain"
        style={{
          filter: filters.length ? filters.join(' ') : undefined,
          opacity: isPostponed ? 0.7 : (grayscale ? 0.6 : 1)
        }}
        onError={() => setHasError(true)}
      />
    );
  }

  // Kruhový rámeček (s bílým okrajem) ořezává obrázek. Národní vlajky jsou
  // v PNG vykreslené přes celou šířku, ale s průhledným pruhem nahoře/dole
  // (letterbox), takže by v kruhu zůstaly bílé okraje. Pro vlajky proto obrázek
  // přizoomujeme (`is-flag`), aby vyplnil celý kruh. Klubová loga vyplňují
  // čtverec sama, takže je necháme bez zoomu.
  return (
    <span
      className={`team-badge-circle ${isFlag ? 'is-flag' : ''}`}
      style={shadow ? { boxShadow: shadow } : undefined}
    >
      <img
        src={badgePath}
        alt={`Badge for ${teamName}`}
        className="team-badge-img"
        style={{
          filter: (isPostponed || grayscale) ? 'grayscale(100%)' : 'none',
          opacity: isPostponed ? 0.7 : (grayscale ? 0.6 : 1),
          // Vlajku přizoomujeme přesně podle změřeného letterboxu, ať vyplní
          // kruh. Než se hodnota načte, drží se výchozí scale(1.5) z CSS.
          transform: (isFlag && flagScale) ? `scale(${flagScale})` : undefined
        }}
        onError={() => setHasError(true)}
      />
    </span>
  );
};

export default TeamBadge;
